import * as L from 'leaflet';
import { FeedLegendConfig, FeedSection, HelperService } from '../../../services/helper.service';
import { FeedFilterService } from '../../../services/feed-filter.service';
import { ViewportWfsService } from './viewport-wfs.service';
import { FeedLegendSummaryController, LegendFilters } from './feed-legend-summary.controller';

export type FeedLegendPane = 'normal' | 'left' | 'right';

export interface FeedLegendHost {
  getContext(storageKey: string, geoKey: string): {
    config?: FeedLegendConfig;
    originalResults: any;
  };
  getColor(geoKey: string): string | undefined;
  getFilters(geoKey: string): LegendFilters;
  publish(geoKey: string, pane: FeedLegendPane): void;
}

interface FeedLegendSource {
  geoKey: string;
  config: FeedLegendConfig;
  originalResults: any;
  pane: FeedLegendPane;
  detach?: () => void;
}

/** Component-owned legend state shared by normal and comparison views. */
export class FeedLegendController {
  private readonly sources = new Map<string, FeedLegendSource>();
  private readonly summaries: FeedLegendSummaryController;

  constructor(
    wfs: ViewportWfsService,
    filters: FeedFilterService,
    private readonly helper: HelperService,
    private readonly host: FeedLegendHost
  ) {
    this.summaries = new FeedLegendSummaryController(wfs, filters, (key) => this.publish(key));
  }

  register(storageKey: string, geoKey: string, layerName: string, baseCql: string | null, filterKey?: string, wmsLayer?: L.Layer): void {
    this.remove(storageKey);
    const { config, originalResults } = this.host.getContext(storageKey, geoKey);
    if (!config?.type || !['Ship', 'Vessel', 'Transhipment', 'Transshipment', 'Vessel-classification', 'Vehicle-classification', 'Aircraft'].includes(config.type)) return;
    const pane: FeedLegendPane = storageKey.endsWith('::compare::left') ? 'left'
      : storageKey.endsWith('::compare::right') ? 'right' : 'normal';
    const source: FeedLegendSource = {
      geoKey, config, pane, originalResults: JSON.parse(JSON.stringify(originalResults)),
    };
    this.sources.set(storageKey, source);
    const removed = () => {
      if (this.sources.get(storageKey) !== source) return;
      this.remove(storageKey);
    };
    wmsLayer?.on('remove', removed);
    source.detach = () => wmsLayer?.off('remove', removed);
    const originalCount = this.helper.getFeedResultCount(originalResults, config.type);
    this.summaries.register({ storageKey, layerName, baseCql, filterKey, feedType: config.type }, originalCount);
    this.summaries.refresh(storageKey, this.host.getFilters(geoKey));
  }

  has(storageKey: string): boolean {
    return this.sources.has(storageKey);
  }

  panes(geoKey: string): FeedLegendPane[] {
    return Array.from(this.sources.values())
      .filter((source) => source.geoKey === geoKey)
      .map((source) => source.pane);
  }

  sections(key: string, pane: FeedLegendPane, sections: FeedSection[]): FeedSection[] {
    const entry = Array.from(this.sources.entries()).find(([, source]) => source.geoKey === key && source.pane === pane);
    if (!entry) return sections;
    const [storageKey, source] = entry;
    this.summaries.refresh(storageKey, this.host.getFilters(key));
    const state = this.summaries.state(storageKey);
    if (!state) return sections;
    if (state.status === 'ready' && state.original) {
      return this.helper.buildFeedSections(
        source.config, source.originalResults, this.host.getColor(key)
      ).sections;
    }
    const filters = this.host.getFilters(key);
    const total = state.status === 'ready' ? state.total : state.status === 'loading' ? 'Loading…' : 'Unavailable';
    const countKey = this.helper.getFeedResultCountKey(source.config.type);
    if (!countKey) return sections;
    const results = { ...(typeof source.originalResults === 'object' ? source.originalResults : {}), [countKey]: total };
    const classification = source.config.type === 'Vessel-classification' ||
      source.config.type === 'Vehicle-classification';
    const classCounts = !classification ? undefined : state.status === 'ready' ? state.classCounts
      : Object.fromEntries(Object.keys(source.config.color ?? {}).map((classKey) => [classKey, total]));
    return this.helper.buildFeedSections(
      source.config, results, this.host.getColor(key),
      filters.classFilter ? { type: 'classBasedFilter', value: String(filters.classFilter.value).trim() } : undefined,
      classCounts, total
    ).sections;
  }

  refresh(): void {
    this.sources.forEach((source, key) => {
      this.summaries.refresh(key, this.host.getFilters(source.geoKey));
      this.publish(key);
    });
  }

  remove(storageKey: string): void {
    this.summaries.remove(storageKey);
    this.sources.get(storageKey)?.detach?.();
    this.sources.delete(storageKey);
  }

  removeFeed(geoKey: string, pane?: FeedLegendPane): void {
    this.sources.forEach((source, key) => {
      if (source.geoKey === geoKey && (!pane || source.pane === pane)) this.remove(key);
    });
  }

  clearPane(pane: FeedLegendPane): void {
    this.sources.forEach((source, key) => {
      if (source.pane === pane) this.remove(key);
    });
  }

  clear(): void {
    Array.from(this.sources.keys()).forEach((key) => this.remove(key));
  }

  private publish(storageKey: string): void {
    const source = this.sources.get(storageKey);
    if (source) this.host.publish(source.geoKey, source.pane);
  }
}
